import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, Spinner, Typography } from '@glplus/glpweb-dsl';
import { Panel } from '../components/Panel';
import { runnerApi, RunnerApiError, type CoverOffer } from '../lib/runnerApiClient';

/**
 * §6b Open runs — work another Runner could not keep.
 *
 * 🔒 EVERY PART OF THIS PAGE REPEATS THAT COVERING COSTS NO LIMIT (Rod,
 * 2026-09-12: *"we would not count pick ups from here against a runners
 * limit"*). It is not decoration. A Runner sitting at their weekly ceiling will
 * otherwise assume these offers are not meant for them — and the zones where
 * cover is hardest to find are exactly the zones where Runners are at their
 * ceiling.
 *
 * 🛑 NOTHING HERE NAGS. A Runner who passes, or who never opens the page, has
 * done nothing wrong: passing on an open run is listed under what is NOT a mark
 * on "Your record", and this copy has to agree with that.
 *
 * 🪤 Accepting IS allowed from the desk, unlike the field actions on Runs.
 * Taking a run is a scheduling decision, not a field action — a Runner deciding
 * their week at a laptop is exactly who this page is for.
 */

const ORANGE = '#F67A1A';
const NAVY = '#343F5B';

/** "4–9 PM", saying the meridiem once when both ends share it. */
function windowLabel(startHhmm: string, endHhmm: string, locale: string): string {
  const clock = (hhmm: string) =>
    new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: hhmm.slice(3) === '00' ? undefined : '2-digit', timeZone: 'UTC' })
      .format(new Date(`1970-01-01T${hhmm}:00.000Z`));
  const start = clock(startHhmm);
  const end = clock(endHhmm);
  const suffix = start.slice(-2);
  return suffix === end.slice(-2) ? `${start.slice(0, -3)}–${end}` : `${start}–${end}`;
}

function dayLabel(ymd: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${ymd}T00:00:00.000Z`));
}

/**
 * 🪤 Minutes under the hour. A same-day absence leaves an offer with minutes on
 * it, and "0 hours left" is how a Runner decides not to bother with something
 * they could still take.
 */
export function timeLeft(expiresAt: string, t: (k: string, o?: Record<string, unknown>) => string, now = new Date()): string {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (ms <= 0) return t('openRuns.closed');
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return t('openRuns.minutesLeft', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('openRuns.hoursLeft', { count: hours });
  return t('openRuns.daysLeft', { count: Math.round(hours / 24) });
}

export default function OpenRuns() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [offers, setOffers] = useState<CoverOffer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [taken, setTaken] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<CoverOffer | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await runnerApi.coverOffers();
      setOffers(d.offers);
      setError(null);
    } catch (e) {
      // 🛑 A failed load renders the failure, never "nothing open". Telling a
      // Runner the board is empty when the request died costs somebody cover.
      setError(e instanceof RunnerApiError ? e.message : t('openRuns.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = useMemo(
    () => (offers ?? []).filter((o) => o.status === 'open' && new Date(o.expiresAt).getTime() > Date.now()),
    [offers],
  );

  async function accept(offer: CoverOffer) {
    setBusy(offer.id);
    setError(null);
    try {
      await runnerApi.acceptCover(offer.id);
      setAccepted(offer);
      await load();
    } catch (e) {
      // 🪤 409 is not an error. Somebody was faster, which is the marketplace
      // working — the Runner is told plainly and loses nothing.
      if (e instanceof RunnerApiError && e.status === 409) {
        setTaken(offer.id);
        await load();
        return;
      }
      setError(e instanceof RunnerApiError ? e.message : t('openRuns.acceptFailed'));
    } finally {
      setBusy(null);
    }
  }

  async function pass(offer: CoverOffer) {
    setBusy(offer.id);
    try {
      await runnerApi.declineCover(offer.id);
    } catch {
      // Passing is a courtesy to the next Runner, not a transaction.
    } finally {
      setBusy(null);
      await load();
    }
  }

  if (offers === null && !error) {
    return (
      <Panel title={t('openRuns.title')} accent={ORANGE}>
        <Spinner aria-label={t('openRuns.loading')} />
      </Panel>
    );
  }

  if (accepted) {
    return (
      <Panel title={t('openRuns.title')} accent="#2E7D32" dataTestId="open-runs-accepted">
        <Typography sx={{ fontWeight: 700, fontSize: '20px', color: '#1f2937' }}>
          {t('openRuns.yoursTitle', { window: `${dayLabel(accepted.runDate, locale)} · ${windowLabel(accepted.deliveryWindowStart, accepted.deliveryWindowEnd, locale)}` })}
        </Typography>
        <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 0.5 }}>
          {accepted.zone} · {t('openRuns.orders', { count: accepted.orderCount })}
        </Typography>
        <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 1.5 }}>{t('openRuns.yoursBody')}</Typography>
        <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, backgroundColor: '#E7F5ED' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1B5E37' }}>{t('openRuns.noLimitTitle')}</Typography>
          <Typography sx={{ fontSize: '13px', color: '#1B5E37', mt: 0.5 }}>{t('openRuns.noLimitBodyAccepted')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
          <Button ariaLabel={t('openRuns.seeRuns')} buttonText={t('openRuns.seeRuns')} variant="contained" size="small" onClick={() => { window.location.href = '/runs'; }} />
          <Button ariaLabel={t('openRuns.back')} buttonText={t('openRuns.back')} variant="text" size="small" onClick={() => setAccepted(null)} />
        </Box>
      </Panel>
    );
  }

  return (
    <Panel title={t('openRuns.title')} accent={ORANGE} headingLevel="h1" dataTestId="page-open-runs">
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2 }}>{t('openRuns.subtitle')}</Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" data-testid="open-runs-error">{error}</Alert>
        </Box>
      )}

      {open.length === 0 ? (
        <Box data-testid="open-runs-empty" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 1.75, py: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{t('openRuns.emptyTitle')}</Typography>
          <Typography sx={{ color: '#5f6b7a', fontSize: '14px', mt: 0.5 }}>{t('openRuns.emptyBody')}</Typography>
        </Box>
      ) : (
        <Box data-testid="open-runs-list" sx={{ display: 'grid', gap: 1.5 }}>
          {open.map((o) => (
            <Box
              key={o.id}
              data-testid={`open-run-${o.id}`}
              sx={{
                border: '1px solid #e5e7eb',
                borderLeft: `4px solid ${taken === o.id ? '#9aa3b2' : ORANGE}`,
                borderRadius: 2,
                px: 2,
                py: 1.75,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ minWidth: 240 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '16px', color: NAVY }}>
                    {dayLabel(o.runDate, locale)} · {windowLabel(o.deliveryWindowStart, o.deliveryWindowEnd, locale)}
                  </Typography>
                  <Chip
                    label={taken === o.id ? t('openRuns.takenPill') : t('openRuns.needsCoverPill')}
                    size="small"
                    sx={{
                      backgroundColor: taken === o.id ? '#EEF1F6' : '#FDF3E1',
                      color: taken === o.id ? '#5f6b7a' : '#7A4A08',
                      fontWeight: 700,
                      fontSize: '11px',
                    }}
                  />
                </Box>
                <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mt: 0.25 }}>
                  {o.zone} · {t('openRuns.orders', { count: o.orderCount })}
                </Typography>
                <Typography sx={{ color: taken === o.id ? '#5f6b7a' : '#B45309', fontSize: '12px', fontWeight: 600, mt: 0.5 }}>
                  {taken === o.id ? t('openRuns.takenBody') : timeLeft(o.expiresAt, t)}
                </Typography>
              </Box>
              {taken === o.id ? null : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    ariaLabel={t('openRuns.accept')}
                    dataTestId={`open-run-take-${o.id}`}
                    buttonText={t('openRuns.accept')}
                    variant="contained"
                    size="small"
                    disabled={busy !== null}
                    onClick={() => void accept(o)}
                  />
                  <Button
                    ariaLabel={t('openRuns.pass')}
                    dataTestId={`open-run-pass-${o.id}`}
                    buttonText={t('openRuns.pass')}
                    variant="text"
                    size="small"
                    disabled={busy !== null}
                    onClick={() => void pass(o)}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* 🔒 The ruling, on the page where the decision is made. */}
      <Box data-testid="open-runs-no-limit" sx={{ mt: 2.5, p: 1.75, borderRadius: 2, backgroundColor: '#E7F5ED' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1B5E37' }}>{t('openRuns.noLimitTitle')}</Typography>
        <Typography sx={{ fontSize: '13px', color: '#1B5E37', mt: 0.5 }}>{t('openRuns.noLimitBody')}</Typography>
      </Box>

      <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 2 }}>{t('openRuns.footer')}</Typography>
    </Panel>
  );
}
